import { useCallback, useMemo, useRef } from 'react';
import {
  enablePaymentExpirationReminders,
  getHideNotificationsOnParentOffice,
} from '~/growthbook';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { useGetPaymentMethods } from './useGetPaymentMethods';

export const useCheckPaymentExpiration = (officeId?: string) => {
  const isParentOffice = useIsParentOfficeSelector();
  const user = useSelectedOffice(officeId);

  const isEnablePaymentExpiration = useRef(enablePaymentExpirationReminders()).current;
  const isHideNotificationsOnParentOffice = useRef(getHideNotificationsOnParentOffice()).current;

  const isParentOfficeAndHideNotificationsOnParentOffice =
    isParentOffice && isHideNotificationsOnParentOffice;
  const shouldFetchPayments = Boolean(
    isEnablePaymentExpiration &&
    !user?.is_pre_approved &&
    !isParentOfficeAndHideNotificationsOnParentOffice,
  );

  const {
    paymentMethods: payments,
    isError: isPaymentMethodsError,
    isFetched: hasFetchedPaymentMethods,
  } = useGetPaymentMethods(officeId, {
    enabled: shouldFetchPayments,
  });
  const hasResolvedPaymentMethods =
    !shouldFetchPayments || hasFetchedPaymentMethods || isPaymentMethodsError;

  const defaultPaymentMethod = useMemo(
    () => payments?.find((payment) => payment.is_selected),
    [payments],
  );

  const isBankAccount = defaultPaymentMethod?.type === 'bank_account';
  const validPayments = useMemo(
    () => payments?.filter((payment) => payment.status !== 'pending'),
    [payments],
  );

  const checkPaymentExpiration = useCallback((payment: { type?: string; expiry_year?: number; expiry_month?: number } | undefined) => {
    const isPaymentBankAccount = payment?.type === 'bank_account';
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const expiryYear = payment?.expiry_year ?? 0;
    const expiryMonth = payment?.expiry_month ?? 0;

    const isExpired =
      (expiryYear < currentYear ||
        (expiryYear === currentYear && expiryMonth < currentMonth)) &&
      !isPaymentBankAccount;

    let isExpiring = false;

    if (!isExpired) {
      const monthsUntilExpiry =
        (expiryYear - currentYear) * 12 + (expiryMonth - currentMonth);
      isExpiring = monthsUntilExpiry <= 2 && !isPaymentBankAccount;
    }

    return {
      isExpired,
      isExpiring,
    };
  }, []);

  const { isExpired, isExpiring } = useMemo(
    () => checkPaymentExpiration(defaultPaymentMethod),
    [checkPaymentExpiration, defaultPaymentMethod],
  );

  const isInvalidPayment =
    !isParentOffice &&
    !user?.is_pre_approved &&
    hasResolvedPaymentMethods &&
    (isExpired || !validPayments?.length);

  if (
    !isEnablePaymentExpiration ||
    user?.is_pre_approved ||
    isParentOfficeAndHideNotificationsOnParentOffice ||
    isBankAccount
  ) {
    return {
      isExpired: false,
      isExpiring: false,
      checkPaymentExpiration,
      payments,
      isInvalidPayment,
    };
  }

  return {
    isExpired,
    isExpiring,
    checkPaymentExpiration,
    payments,
    isInvalidPayment,
  };
};
